import { createContext, useState, useContext, ReactNode, useCallback } from 'react';
import _ from 'lodash';
import { UserSettings } from '@/types/types';

export interface User {
  name: string;
  email: string;
  picture: string;
  hlsStreamUrl: string;
  settings: UserSettings;
  autoPlay: boolean;
}


export interface UserContextType {
  user: User | null;
  setUser: (user: User | null) => void;
  addUserChangeListener: (listener: (user: User | null) => void) => void;
  removeChangeListener: (listener: (user: User | null) => void) => void;
}

const userContext = createContext<UserContextType | undefined>(undefined);

export const UserProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [listeners] = useState<((newUser: User | null) => void)[]>([]);

  const notifyListeners = useCallback((newUser: User | null) => {
    listeners.forEach(listener => listener(newUser));
  }, [listeners]);

  const updateUser = useCallback((newUser: User | null) => {
    // Compare all fields of the user object
    const compareUsers = (user1: User | null, user2: User | null) => {
      if (user1 === null && user2 === null) return true;
      if (user1 === null || user2 === null) return false;
      return _.isEqual(user1, user2);
    };
    if (compareUsers(user, newUser)) return;

    setUser(newUser);
    notifyListeners(newUser);
  }, [notifyListeners, user]);

  const addChangeListener = useCallback((listener: (newUser: User | null) => void) => {
    if (listeners.find(l => l === listener)) return;

    listeners.push(listener);
  }, [listeners]);

  const removeChangeListener = useCallback((listener: (newUser: User | null) => void) => {
    const index = listeners.indexOf(listener);
    if (index > -1) {
      listeners.splice(index, 1);
    }
  }, [listeners]);

  return (
    <userContext.Provider
      value={{
        user,
        setUser: updateUser,
        addUserChangeListener: addChangeListener,
        removeChangeListener,
      }}
    >
      {children}
    </userContext.Provider>
  );
};

export const useUser = () => {
  const context = useContext(userContext);
  if (!context) {
    throw new Error('useUser must be used within a UserProvider');
  }
  return context;
};